// bankDoorSignature — the door value the wizard actually persists.
//
// A bank's doors live PER FLOOR, in bank_floors[].front_door / .rear_door.
// That per-floor array is the only door data PUT /banks/{bankID} writes —
// StepBanks' handleUpdateBank sets body.bank_floors, and the backend persists
// it via updateBankFloors. The bank-level front_doors / rear_doors columns
// are absent from that PUT body entirely, and absent from all three of
// LiftCloud's update paths (updateBankWithoutAdapter, updateBankPreservingAdapter,
// updateBankWithAdapter each build an `updates` map with no door key — verified
// against bank.controller.go; the fieldFrontDoors / fieldRearDoors constants in
// json_field_names.go have no callers at all). They are set only on bank
// creation (handleAddBank's body) and never corrected afterward.
//
// So bank.front_doors / bank.rear_doors are write-once at create time and
// frozen thereafter. They are a summary that a later floor edit silently
// contradicts — the LIFT-523 report was a bank whose every floor had
// rear_door true while its own rear_doors column read false. Diffing that
// frozen column on one snapshot side against a floor-derived value on the
// other would not self-heal once and stop — it would recur on every
// subsequent save of a drifted bank, forever, since the save never corrects
// the column.
//
// The signatures below are what the wizard-invalidation snapshots diff
// instead. They are per-floor and keyed/sorted by floorIndex alone — not by
// any row id — because production's bankFloorDoorRows sorts by floorIndex
// only (StepBanks.tsx), with no id tiebreak; using an id-based tiebreak or a
// positional (`bf-${idx}`-style) synthetic key here would let the pre-save
// and post-save signatures for an UNCHANGED bank come out ordered
// differently and produce a spurious invalidation — the exact bug class this
// module exists to eliminate. floorIndex is already the row's semantic
// identity, so it is both the sort key and the signature key.

import { readDoorFlags, readFloorIndexField } from "../../../utils/bankFloorOpenings";

/** A floor row as StepBanks normalizes it. Declared structurally so this
 *  module never imports from ../steps — utils must not depend on steps. */
export interface BankDoorFloorRow {
  id: string;
  floorIndex: number;
  frontDoor: boolean;
  rearDoor: boolean;
}

/** Per-side door signatures, each `"<floorIndex>:1"` / `"<floorIndex>:0"`
 *  ordered by floorIndex ascending. Two independent arrays rather than one
 *  combined structure so BANK_FIELD_RULES keeps per-field granularity:
 *  frontDoors and rearDoors stay separately diffable even though both
 *  currently map to the same step. */
export interface BankDoorSignature {
  front: string[];
  rear: string[];
}

// Frozen so the two functions below can share one instance for the empty
// case without risking a future caller mutating it (e.g. `signature.front.push(...)`)
// and silently corrupting every other empty-bank comparison for the rest of
// the page's lifetime — ES modules are singletons, and both `front`/`rear`
// are otherwise plain mutable `string[]`.
const EMPTY_SIGNATURE: BankDoorSignature = Object.freeze({ front: [], rear: [] });

type NormalizedRow = Omit<BankDoorFloorRow, "id">;

/**
 * Resolve one raw floor object's floorIndex/frontDoor/rearDoor. Exported (not
 * just used internally) so `StepBanks.tsx`'s own `bankFloorDoorRows` builder
 * calls this directly instead of hand-duplicating a fallback chain a second
 * time — a drift between two independent copies is exactly the kind of
 * phantom-invalidation bug this module exists to eliminate.
 *
 * WHICH keys name a field and HOW a raw value coerces to a boolean are
 * resolved by `utils/bankFloorOpenings` — the same module the monitoring side
 * reads these same rows through (`readDoorFlags`, `readFloorIndexField`) — so
 * a casing or a numeric-0 flag cannot mean one thing to the wizard and
 * another to the monitoring panel. That module's coercion (`asBool`) is also
 * STRICTER than this function's old bare `Boolean(...)`: a stray non-boolean,
 * non-number value (a string, an object) now falls back to
 * `fallbackFront`/`fallbackRear` instead of `Boolean` unconditionally reading
 * it as `true` — `Boolean("false")` is `true`, which is the bug the shared
 * reader closes for both callers at once. This is an intentional behavior
 * change, not an oversight.
 *
 * WHAT AN ABSENT FIELD MEANS stays decided here, not in the shared module: a
 * row with no door value falls back to the BANK-LEVEL column the caller
 * passes in, never to `extractBankFloorOpenings`'s own front-open/rear-closed
 * assumption (`readDoorFlags` takes the fallback as a parameter for exactly
 * this reason), and a row with no floorIndex falls back to its ARRAY
 * POSITION (`idx`), not to `extractBankFloorOpenings`'s NaN-then-dropped
 * handling. `extractBankFloorOpenings` itself is deliberately never called
 * here — it also drops rows it cannot identify by id/position, and a door
 * signature that silently loses a floor invalidates nothing.
 */
export function resolveBankFloorDoorFields(
  raw: unknown,
  idx: number,
  fallbackFront: boolean,
  fallbackRear: boolean,
): NormalizedRow {
  const doors = readDoorFlags(raw, fallbackFront, fallbackRear);

  // Coerced separately from `?? idx` rather than `Number(x ?? idx) || 0`: the
  // combined form maps ANY non-numeric-but-present floorIndex (a corrupt or
  // malformed value — `??` only catches null/undefined, never a string) to
  // `0` via `NaN || 0`, silently colliding with a floor whose real index IS
  // 0, instead of falling back to the row's array position like an absent
  // value does. `idx` is used only when the row's own value is genuinely
  // missing or fails to coerce — never as a blanket default for "any falsy
  // result".
  const rawFloorIndex = readFloorIndexField(raw);
  const coercedFloorIndex =
    typeof rawFloorIndex === "number" ? rawFloorIndex : Number(rawFloorIndex);
  const floorIndex =
    rawFloorIndex === undefined || rawFloorIndex === null || Number.isNaN(coercedFloorIndex)
      ? idx
      : coercedFloorIndex;

  return {
    floorIndex,
    frontDoor: doors.front,
    rearDoor: doors.rear,
  };
}

function toSignature(rows: readonly NormalizedRow[]): BankDoorSignature {
  const ordered = [...rows].sort((a, b) => a.floorIndex - b.floorIndex);
  const front: string[] = [];
  const rear: string[] = [];
  ordered.forEach((r) => {
    front.push(`${r.floorIndex}:${r.frontDoor ? 1 : 0}`);
    rear.push(`${r.floorIndex}:${r.rearDoor ? 1 : 0}`);
  });
  return { front, rear };
}

/**
 * Build the signature from a raw bank payload's `bank_floors` / `bankFloors`,
 * via `resolveBankFloorDoorFields` — the single field-resolution chain this
 * module and `StepBanks.tsx`'s row builder both call, so the pre-save and
 * post-save snapshots stay structurally comparable by construction rather
 * than by a "please keep these in sync by hand" comment.
 *
 * A bank with no floors (escalator / moving-walk / GPIO, or a bank whose
 * floors were never created) yields empty arrays. That is correct rather
 * than lossy: such a bank persists no door data on save, so there is nothing
 * a downstream step could need re-verified.
 */
export function bankDoorSignatureFromRawFloors(
  raw: unknown,
  fallbackFront: boolean,
  fallbackRear: boolean,
): BankDoorSignature {
  if (!Array.isArray(raw)) return EMPTY_SIGNATURE;
  const rows = raw.map((bf, idx) =>
    resolveBankFloorDoorFields(bf, idx, fallbackFront, fallbackRear),
  );
  return toSignature(rows);
}

/** Build the signature from already-normalized rows — the post-save side,
 *  where StepBanks' bankFloorDoorRows is the value about to be written as
 *  body.bank_floors. Re-sorts internally by floorIndex rather than trusting
 *  incoming order, so this function is correct standalone even though
 *  production already sorts its input the same way. */
export function bankDoorSignatureFromRows(
  rows: readonly BankDoorFloorRow[] | null | undefined,
): BankDoorSignature {
  if (!rows) return EMPTY_SIGNATURE;
  return toSignature(rows);
}
